import { Link } from 'react-router-dom';
import type { WorldBossLeaderboardRow } from '../../lib/types';
import { TrainerAvatar } from '../avatar/TrainerAvatar';
import { formatNumber } from '../../lib/format';

/**
 * The ladder, and the line across it — design PART 9 and 10.
 *
 * **The cutoff is the feature.** A leaderboard that lists people says who is
 * winning; one that draws a visible line between the last rewarded place and
 * the first unrewarded one says what is at stake, and the difference is what
 * makes somebody spend their last attempt of the evening. So the line is a real
 * element with words on it, not a colour change somebody has to notice.
 *
 * Where the line goes comes from the server (`rewards.zone`), never from a five
 * hardcoded here: PART 41 is explicit that a future boss might pay only #1 or
 * the top ten, and a client that assumed five would draw the line in the wrong
 * place on the first one that did.
 *
 * **Your own row is always visible**, wherever you are (PART 10). Rank 480 is
 * shown with its neighbours in its own panel rather than by making somebody
 * scroll four hundred rows to find themselves.
 */

export function BossLeaderboard({
  rows,
  zone,
  settled,
}: {
  rows: WorldBossLeaderboardRow[];
  zone: { from: number; to: number };
  /** After finalisation the line is history rather than a threat, and the copy
   *  changes to say so. */
  settled: boolean;
}) {
  if (rows.length === 0) {
    return <p className="tiny muted">Nobody has landed a hit yet. Be first.</p>;
  }

  return (
    <ol className="wb-board">
      {rows.map((row, index) => {
        // The line is drawn *after* the last rewarded rank, and only when there
        // is actually somebody below it — a line at the bottom of a list of
        // three is a line about nothing.
        const showCutoff = row.rank === zone.to && index < rows.length - 1;
        return (
          <li key={row.userId} className="wb-board__item">
            <Row row={row} inZone={row.rank >= zone.from && row.rank <= zone.to} />
            {showCutoff && (
              <div className="wb-board__cutoff" role="separator">
                <span>{settled ? 'Legendary reward line' : 'Legendary cutoff'}</span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The viewer's neighbourhood — PART 10. Same rows, no cutoff line. */
export function BossNearby({
  rows,
  zone,
}: {
  rows: WorldBossLeaderboardRow[];
  zone: { from: number; to: number };
}) {
  if (rows.length === 0) return null;
  return (
    <ol className="wb-board wb-board--nearby">
      {rows.map((row) => (
        <li key={row.userId} className="wb-board__item">
          <Row row={row} inZone={row.rank >= zone.from && row.rank <= zone.to} />
        </li>
      ))}
    </ol>
  );
}

function Row({ row, inZone }: { row: WorldBossLeaderboardRow; inZone: boolean }) {
  const classes = [
    'wb-row',
    inZone ? 'wb-row--zone' : '',
    row.isYou ? 'wb-row--you' : '',
    row.rank === 1 ? 'wb-row--first' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <span className="wb-row__rank mono">#{row.rank}</span>

      <TrainerAvatar avatar={row.avatar} size={40} />

      <div className="wb-row__who">
        {/* A real link. A leaderboard is a directory, and the whole reason to
            read one is to go and look at whoever is beating you. */}
        <Link to={`/trainer/${row.username}`} className="wb-row__name">
          {row.trainerName}
        </Link>
        {row.fighter ? (
          <span className="tiny muted">
            {row.fighter.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
            {row.fighter.name} Lv. {row.fighter.level}
          </span>
        ) : (
          <span className="tiny muted">No fighter chosen</span>
        )}
      </div>

      <div className="wb-row__damage">
        <strong className="mono">{formatNumber(row.damage)}</strong>
        {/* PART 9 lists attempts as optional. It is here because it is the
            second tie-break, so somebody looking at two identical totals can
            see which way it would fall. */}
        <span className="tiny muted">
          {row.attempts} {row.attempts === 1 ? 'attempt' : 'attempts'}
        </span>
      </div>
    </div>
  );
}
